import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useSession } from '../../SessionContext';
import { useApiClient } from '../../ApiClientProvider';
import { PodService } from '../../services/podService';
import { getPodHubService } from '../../services/podHubService';
import type { PodMessageCreatedPayload } from '../../services/podHubService';
import { isPodFocused } from '../../services/podReadFocusTracker';
import {
    clearPodUnreadCount,
    incrementPodUnreadCount,
    selectPodUnreadCounts,
    setPodUnreadCounts,
    setPods,
} from '../../redux/podsSlice';
import PodChatWindow from './PodChatWindow';

interface OpenPodChat {
    podId: string;
    isMinimized: boolean;
}

interface PodChatManagerAPI {
    openPod: (podId: string) => void;
    closePod: (podId: string) => void;
}

const WINDOW_WIDTH_WITH_GAP = 380;
const BASE_RIGHT_OFFSET = 120;

const PodChatManager: React.FC = () => {
    const { session } = useSession();
    const currentUserId = session?.user?.id ?? '';
    const authToken = session?.token ?? '';
    const dispatch = useDispatch();
    const podUnreadCounts = useSelector(selectPodUnreadCounts);
    const applicationClient = useApiClient('applicationClient');
    const podService = useMemo(() => new PodService(applicationClient), [applicationClient]);
    const podHubService = useMemo(() => (authToken ? getPodHubService(authToken) : null), [authToken]);

    const [openChats, setOpenChats] = useState<OpenPodChat[]>([]);
    const managerRef = useRef<PodChatManagerAPI | null>(null);
    const pendingUnreadPodIdsRef = useRef<Set<string>>(new Set());
    const podUnreadCountsRef = useRef(podUnreadCounts);

    useEffect(() => {
        podUnreadCountsRef.current = podUnreadCounts;
    }, [podUnreadCounts]);

    const refreshPods = useCallback(async () => {
        try {
            const podsData = await podService.getPods();
            dispatch(setPods(podsData));

            const nextUnreadCounts = podsData.reduce<{ [podId: string]: number }>((acc, pod) => {
                const isFocused = isPodFocused(pod.id);
                const serverUnreadCount = isFocused ? 0 : pod.unreadMessageCount ?? 0;
                const existingUnreadCount = isFocused ? 0 : podUnreadCountsRef.current[pod.id] ?? 0;
                const seededUnreadCount = isFocused || !pendingUnreadPodIdsRef.current.has(pod.id) ? 0 : 1;

                acc[pod.id] = Math.max(serverUnreadCount, existingUnreadCount, seededUnreadCount);
                return acc;
            }, {});

            pendingUnreadPodIdsRef.current.clear();

            dispatch(
                setPodUnreadCounts(nextUnreadCounts)
            );
        } catch (error) {
            console.error('PodChatManager: failed to refresh pods', error);
        }
    }, [dispatch, podService]);

    // Hydrate unread counts (and the pods list) on session load so badges are
    // available app-wide, not just when the Pods page has been visited.
    // Pods the user is currently viewing stay at zero: their mark-read may
    // still be in flight server-side, and showing a badge for a conversation
    // that is open on screen would be wrong.
    useEffect(() => {
        if (!authToken || !currentUserId) return;

        void refreshPods();
    }, [authToken, currentUserId, refreshPods]);

    // Single global real-time listener: the source of truth for pod unread
    // badges. Bumps the badge for pods the user isn't currently focused on
    // (via the floating chat window or the inline PodsPage chat tab), or
    // clears + persists read state for pods that are currently focused.
    useEffect(() => {
        if (!podHubService) return;

        void podHubService.start();

        const unsub = podHubService.on('message.created', (payload: PodMessageCreatedPayload) => {
            if (!payload.pod_id || payload.sender_id === currentUserId) return;

            if (isPodFocused(payload.pod_id)) {
                dispatch(clearPodUnreadCount(payload.pod_id));
                void podService.markPodAsRead(payload.pod_id, payload.id).catch(error => {
                    console.error('PodChatManager: markPodAsRead error', error);
                });
                return;
            }

            dispatch(incrementPodUnreadCount(payload.pod_id));
        });

        return unsub;
    }, [currentUserId, dispatch, podHubService, podService]);

    // Keep the sidebar pod list in sync with invitation and leave notifications.
    // The participant hub targets the user directly, so this also covers the
    // moment they are added to a pod and are still not part of that pod's group.
    useEffect(() => {
        const handleNotificationReceived = (event: Event) => {
            const notification = (event as CustomEvent<{ type?: string; relatedEntityId?: string | null }>).detail;
            if (notification?.type === 'PodMemberAdded' && notification.relatedEntityId && !isPodFocused(notification.relatedEntityId)) {
                pendingUnreadPodIdsRef.current.add(notification.relatedEntityId);
                dispatch(incrementPodUnreadCount(notification.relatedEntityId));
                void refreshPods();
                return;
            }

            if (notification?.type === 'PodMemberLeft') {
                void refreshPods();
            }
        };

        const handleNotificationsResync = () => {
            void refreshPods();
        };

        window.addEventListener('odyssey:notification-received', handleNotificationReceived);
        window.addEventListener('odyssey:notifications-resync', handleNotificationsResync);

        return () => {
            window.removeEventListener('odyssey:notification-received', handleNotificationReceived);
            window.removeEventListener('odyssey:notifications-resync', handleNotificationsResync);
        };
    }, [dispatch, refreshPods]);

    // Badge safety net: messages broadcast while the socket was down are never
    // replayed by SignalR, so real-time increments can silently go missing.
    // On every reconnect, reconcile the badges with the server's persisted
    // unread counts (keeping currently-viewed pods at zero).
    useEffect(() => {
        if (!podHubService || !currentUserId) return;

        const unsub = podHubService.onReconnected(() => {
            void podService.getPods()
                .then(podsData => {
                    dispatch(setPods(podsData));
                    dispatch(
                        setPodUnreadCounts(
                            podsData.reduce<{ [podId: string]: number }>((acc, pod) => {
                                acc[pod.id] = isPodFocused(pod.id) ? 0 : pod.unreadMessageCount ?? 0;
                                return acc;
                            }, {})
                        )
                    );
                })
                .catch(error => {
                    console.error('PodChatManager: failed to resync pod unread counts', error);
                });
        });

        return unsub;
    }, [currentUserId, dispatch, podHubService, podService]);

    const openPod = useCallback((podId: string) => {
        setOpenChats(current => {
            if (current.some(chat => chat.podId === podId)) {
                return current.map(chat => (chat.podId === podId ? { ...chat, isMinimized: false } : chat));
            }
            return [...current, { podId, isMinimized: false }];
        });
    }, []);

    const closePod = useCallback((podId: string) => {
        setOpenChats(current => current.filter(chat => chat.podId !== podId));
    }, []);

    const toggleMinimize = useCallback((podId: string) => {
        setOpenChats(current =>
            current.map(chat => (chat.podId === podId ? { ...chat, isMinimized: !chat.isMinimized } : chat))
        );
    }, []);

    useEffect(() => {
        managerRef.current = { openPod, closePod };
        window.podChatManager = managerRef.current;

        return () => {
            if (window.podChatManager === managerRef.current) {
                window.podChatManager = undefined;
            }
        };
    }, [openPod, closePod]);

    const windows = useMemo(
        () =>
            openChats.map((chat, index) => (
                <PodChatWindow
                    key={chat.podId}
                    podId={chat.podId}
                    currentUserId={currentUserId}
                    authToken={authToken}
                    isMinimized={chat.isMinimized}
                    onClose={closePod}
                    onMinimize={toggleMinimize}
                    style={{ right: BASE_RIGHT_OFFSET + index * WINDOW_WIDTH_WITH_GAP }}
                />
            )),
        [openChats, currentUserId, authToken, closePod, toggleMinimize]
    );

    if (!authToken || !currentUserId) return null;

    return createPortal(<div>{windows}</div>, document.body);
};

declare global {
    interface Window {
        podChatManager?: PodChatManagerAPI;
    }
}

export default PodChatManager;
